// v191 — Per-project License-Compliance dashboard (item 15 of
// docs/PRODUCT-ROADMAP.md).
//
// Shows the license distribution across every artifact in the
// project's repos plus an inline editor for the project policy
// (license -> allow|warn|block) and an enforce-on-upload toggle.
// Operator workflow:
//
//   1. Look at the distribution. AGPL-3.0 spotted? Click the row's
//      "Set policy" button to map it to "block".
//   2. Toggle "Enforce on upload" once the policy reflects the
//      desired posture. The next push of an artifact whose license
//      maps to "block" will get a 403.

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Save, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { useProjects } from '../../api/client';
import {
  type LicenseAction,
  type LicensePolicyResponse,
  decideAction,
  putLicensePolicy,
  useLicenseAggregation,
  useLicensePolicy,
} from '../../api/licenseCompliance';

const ACTIONS: LicenseAction[] = ['allow', 'warn', 'block'];

function actionTone(a: LicenseAction): 'success' | 'warning' | 'danger' {
  switch (a) {
    case 'allow':
      return 'success';
    case 'warn':
      return 'warning';
    case 'block':
      return 'danger';
  }
}

const selectClass =
  'h-8 px-2 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

export function LicenseCompliance() {
  const { id } = useParams<{ id: string }>();
  const { data: projects } = useProjects();
  const project = projects?.find((p) => p.id === id);

  const { data: agg, error: aggErr, isLoading: aggLoading } = useLicenseAggregation(id);
  const {
    data: policyData,
    error: policyErr,
    mutate: mutatePolicy,
    isLoading: policyLoading,
  } = useLicensePolicy(id);

  // Local edits — copy server state into local state on first load,
  // then keep the user's draft until they save or cancel.
  const [draft, setDraft] = useState<Record<string, LicenseAction> | null>(null);
  const [enforceDraft, setEnforceDraft] = useState<boolean | null>(null);
  const [newKey, setNewKey] = useState('');
  const [newAction, setNewAction] = useState<LicenseAction>('warn');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const baseline: LicensePolicyResponse = useMemo(
    () => policyData ?? { policy: {}, enforce: false },
    [policyData],
  );
  const policy = draft ?? baseline.policy;
  const enforce = enforceDraft ?? baseline.enforce;
  const dirty =
    JSON.stringify(policy) !== JSON.stringify(baseline.policy) ||
    enforce !== baseline.enforce;

  const setRule = (license: string, action: LicenseAction | null) => {
    const next: Record<string, LicenseAction> = { ...policy };
    if (action === null) {
      delete next[license];
    } else {
      next[license] = action;
    }
    setDraft(next);
  };

  const onSave = async () => {
    if (!id) return;
    setBusy(true);
    setErr(null);
    try {
      await putLicensePolicy(id, policy, enforce);
      await mutatePolicy();
      setDraft(null);
      setEnforceDraft(null);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCancel = () => {
    setDraft(null);
    setEnforceDraft(null);
    setErr(null);
  };

  const onAddDirect = () => {
    const k = newKey.trim();
    if (!k) return;
    setRule(k, newAction);
    setNewKey('');
    setNewAction('warn');
  };

  const isForbidden = policyErr && String(policyErr).includes('403');

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to projects
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">
          {project?.slug ?? id} · Compliance
        </p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          License compliance
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">
          Review the SPDX-style license distribution across every artifact in
          this project's repos, then steer uploads with a per-license rule.
          Pulls are never affected — the enforce toggle gates the upload path
          only.
        </p>
      </div>

      {isForbidden && (
        <Card className="border-warning/30 bg-warning/5">
          <CardBody className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
            <div className="text-sm">
              You don't have permission to edit this project's license policy.
              Distribution stays read-only; ask a project maintainer to make
              changes.
            </div>
          </CardBody>
        </Card>
      )}

      {/* ---------- Distribution ---------- */}
      <Card>
        <CardHeader
          title="License distribution"
          description={
            agg
              ? `${agg.total.toLocaleString()} artifacts · ${agg.unknown.toLocaleString()} without a detected license`
              : 'Loading…'
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">License</th>
                <th className="px-4 py-2 font-medium">Count</th>
                <th className="px-4 py-2 font-medium">Effect</th>
                <th className="px-4 py-2 font-medium">Sample paths</th>
                <th className="px-4 py-2 font-medium">Set rule</th>
              </tr>
            </thead>
            <tbody>
              {aggLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={5}>
                    Loading…
                  </td>
                </tr>
              )}
              {aggErr && (
                <tr>
                  <td className="px-4 py-3 text-danger" colSpan={5}>
                    {String(aggErr)}
                  </td>
                </tr>
              )}
              {agg?.licenses.length === 0 && !aggLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={5}>
                    No license metadata extracted yet — upload artifacts in
                    formats that carry license info (npm, RPM, CRAN, …) to
                    populate this table.
                  </td>
                </tr>
              )}
              {agg?.licenses.map((row) => {
                const decided = decideAction(row.license, policy);
                const explicit = !!policy[row.license];
                return (
                  <tr key={row.license} className="border-b border-border/50">
                    <td className="px-4 py-2 font-mono">{row.license}</td>
                    <td className="px-4 py-2 tabular-nums">
                      {row.count.toLocaleString()}
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={actionTone(decided)}>
                        {decided}
                        {!explicit && (
                          <span className="ml-1 text-fg-muted">(default)</span>
                        )}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-fg-muted text-xs">
                      <div className="flex flex-col gap-0.5">
                        {row.sample_paths.map((p) => (
                          <code key={p} className="break-all">{p}</code>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <select
                        className={selectClass}
                        value={explicit ? policy[row.license] : ''}
                        disabled={!!isForbidden}
                        onChange={(e) =>
                          setRule(
                            row.license,
                            e.target.value === ''
                              ? null
                              : (e.target.value as LicenseAction),
                          )
                        }
                      >
                        <option value="">— inherit default —</option>
                        {ACTIONS.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ---------- Policy editor ---------- */}
      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-accent" />
              Policy
            </span>
          }
          description="Per-license rules. The reserved key 'default' covers any license not listed here. Save to apply."
        />
        <CardBody className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              id="enforce"
              type="checkbox"
              className="h-4 w-4"
              disabled={!!isForbidden || policyLoading}
              checked={enforce}
              onChange={(e) => setEnforceDraft(e.target.checked)}
            />
            <label htmlFor="enforce" className="text-sm">
              Enforce on upload — refuses pushes whose license maps to{' '}
              <code className="font-mono mx-1">block</code>. Pulls remain
              unaffected.
            </label>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="px-4 py-2 font-medium">License key</th>
                  <th className="px-4 py-2 font-medium">Action</th>
                  <th className="px-4 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {Object.keys(policy).length === 0 && (
                  <tr>
                    <td className="px-4 py-3 text-fg-muted" colSpan={3}>
                      No rules set — everything resolves to "allow" via the
                      implicit default.
                    </td>
                  </tr>
                )}
                {Object.entries(policy)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([license, action]) => (
                    <tr key={license} className="border-b border-border/50">
                      <td className="px-4 py-2 font-mono">{license}</td>
                      <td className="px-4 py-2">
                        <select
                          className={selectClass}
                          value={action}
                          disabled={!!isForbidden}
                          onChange={(e) =>
                            setRule(license, e.target.value as LicenseAction)
                          }
                        >
                          {ACTIONS.map((a) => (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!!isForbidden}
                          onClick={() => setRule(license, null)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-2">
            <Input
              placeholder="License key (e.g., AGPL-3.0 or 'default')"
              value={newKey}
              disabled={!!isForbidden}
              onChange={(e) => setNewKey(e.target.value)}
              className="flex-1"
            />
            <select
              className={selectClass + ' h-10'}
              value={newAction}
              disabled={!!isForbidden}
              onChange={(e) => setNewAction(e.target.value as LicenseAction)}
            >
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              disabled={!!isForbidden || newKey.trim() === ''}
              onClick={onAddDirect}
            >
              <Plus className="h-4 w-4" />
              Add rule
            </Button>
          </div>
        </CardBody>
        <CardFooter className="flex items-center justify-between">
          <div className="text-xs text-fg-muted">
            {baseline.updated_at && baseline.updated_by_email ? (
              <>
                Last edit: {new Date(baseline.updated_at).toLocaleString()} by{' '}
                <code className="font-mono">{baseline.updated_by_email}</code>
              </>
            ) : (
              <>No prior edits — this is the first time the policy is being saved.</>
            )}
          </div>
          <div className="flex items-center gap-2">
            {err && <span className="text-danger text-sm">{err}</span>}
            <Button variant="ghost" disabled={!dirty || busy} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!dirty || busy || !!isForbidden}
              onClick={onSave}
            >
              <Save className="h-4 w-4" />
              {busy ? 'Saving…' : 'Save policy'}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
